import DeviceManager from '../utils/device-manager';

class DynamicMap {
    constructor($container) {
        this.$container = $container;
        this.$map = $container.querySelector('.js-map');
        this.$autocompleteField = $container.querySelector('.js-map-filters-address-field');
        this.$geolocation = $container.querySelector('.js-map-filters-address-geolocation');
        this.$storesContainer = $container.querySelector('.js-map-filters-stores');

        const $storeTemplate = this.$storesContainer.querySelector('#map-filters-store-template');
        this.storeTemplateHTML = $storeTemplate.innerHTML;
        $storeTemplate.parentNode.removeChild($storeTemplate);
        
        this.defaultCenter = new google.maps.LatLng(48.8588377, 2.2770203); // Paris
        this.defaultZoom = 14;
        this.clickedZoom = 17;

        this.gmap = null;
        this.autocomplete = null;
        this.currentLocation = null;
        this.placeMarker = null;
        this.markers = [];
        this.bounds = null;
        this.stores = this.getStoresFromWS();

        this.initMap();
        this.initGeolocation();
        this.initAutocomplete();
        this.addStoresInDOM();
        this.updateMarkers();

        if (!DeviceManager.isGreaterThan(DeviceManager.getCurrentDevice(), 'lg')) {
            this.getGeolocation();
        }
    }

    /**
     * Get stores from WebService
     * @return {Array}
     * 
     * store = {
     *   id: 1,
     *   status: 1,
     *   storename: 'Store name',
     *   address: 'Store address',
     *   zipcode: 75000,
     *   city: 'Paris',
     *   latitude: 12345,
     *   longitude: 12345,
     *   openinghours: { 'monday': ['09:00-12:00', '14:00-19:00'], 'tuesday': ... },
     *   location: LatLng,
     *   html: '<div ...></div>',
     * }
     */
    getStoresFromWS() {
        const stores = [
            {
                storename: 'Clignancourt',
                address: '19 rue du Poteau',
                zipcode: '75018',
                city: 'Paris',
                latitude: 48.893905,
                longitude: 2.342083,
            },
            {
                storename: 'Goutte d\'Or',
                address: '76 rue des Poissonniers',
                zipcode: '75018',
                city: 'Paris',
                latitude: 48.892325,
                longitude: 2.351954,
            },
            {
                storename: 'Montmartre',
                address: '14-16 rue d\'Orsel',
                zipcode: '75018',
                city: 'Paris',
                latitude: 48.8840655,
                longitude: 2.3447388,
            },
            {
                storename: 'Sarcelles',
                address: '61 rue Pierre Brossolette',
                zipcode: '95200',
                city: 'Sarcelles',
                latitude: 48.995124,
                longitude: 2.383441,
            },
            {
                storename: 'Le Blanc-Mesnil',
                address: '29 avenue Pierre et Marie Curie',
                zipcode: '93150',
                city: 'Le Blanc-Mesnil',
                latitude: 48.940130,
                longitude: 2.469615,
            },
            {
                storename: 'Saint-Lambert',
                address: '101 rue des Morillons',
                zipcode: '75015',
                city: 'Paris',
                latitude: 48.832236,
                longitude: 2.305619,
            },
        ];

        stores.forEach((store, index) => {
            store.id = index;
            store.status = 1;
            store.openinghours = {
                'monday': ['09:00-12:00', '14:00-19:00'],
                'tuesday': ['09:00-12:00', '14:00-19:00'],
                'wednesday': ['09:00-12:00', '14:00-19:00'],
                'thursday': ['09:00-12:00', '14:00-19:00'],
                'friday': ['09:00-12:00', '14:00-19:00'],
                'saturday': ['09:00-13:00'],
            };
            store.html = this.getStoreHTML(store);
            
            store.location = new google.maps.LatLng(store.latitude, store.longitude);

            store.marker = this.createMarker(store);
            this.markers.push(store.marker);
        });

        return stores;
    }

    /**
     * Get HTML of the store
     * @return {string}
     */
    getStoreHTML(store) {
        let replacements = {
            '_store_id_': store.id,
            '_store_title_': store.storename,
            '_store_address_': store.address + '<br>' + store.zipcode + ' ' + store.city,
            '_store_opening_hours_': '',
        };

        Object.keys(store.openinghours).forEach(day => {
            let frDay = day;
            let openingHour = '';
            
            store.openinghours[day].forEach(oh => {
                openingHour += (openingHour.length > 0 ? ' / ' : '') + oh;
            });

            switch(day) {
                case 'monday': frDay = 'Lundi'; break;
                case 'tuesday': frDay = 'Mardi'; break;
                case 'wednesday': frDay = 'Mercredi'; break;
                case 'friday': frDay = 'Jeudi'; break;
                case 'thursday': frDay = 'Vendredi'; break;
                case 'saturday': frDay = 'Samedi'; break;
                case 'sunday': frDay = 'Dimanche'; break;
            }

            replacements['_store_opening_hours_'] += '<li>' + frDay + ' : ' + openingHour + '</li>';
        });

        let storeHTML = this.storeTemplateHTML;

        Object.keys(replacements).forEach(search => {
            storeHTML = storeHTML.replaceAll(search, replacements[search]);
        });

        return storeHTML;
    }

    /**
     * Get all stores closest to current location
     * @return {Array}
     */
    getStoresClosest() {
        const nbStoresToDisplay = 10;

        let currentLocation = this.currentLocation || this.gmap.getCenter();

        // Sort all stores nearest to farest
        let storesSorted = this.stores.sort((a, b) => this.getDistanceInKmBetween(currentLocation, a.location) - this.getDistanceInKmBetween(currentLocation, b.location));

        // Keep only X nearest stores
        storesSorted = storesSorted.slice(0, nbStoresToDisplay);

        return storesSorted;
    }

    /**
     * Get distance between 2 LatLng Objects in km
     * @param {LatLng} from 
     * @param {LatLng} to 
     */
    getDistanceInKmBetween(from, to) {
        const toRad = value => {
            return value * Math.PI / 180;
        };

        const R = 6371; // km
        const dLat = toRad(to.lat() - from.lat());
        const dLon = toRad(to.lng() - from.lng());
        const fromLat = toRad(from.lat());
        const toLat = toRad(to.lat());

        const a = Math.sin(dLat/2) * Math.sin(dLat/2) + Math.sin(dLon/2) * Math.sin(dLon/2) * Math.cos(fromLat) * Math.cos(toLat); 
        const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1-a)); 
        const d = R * c;

        return d.toFixed(1);
    }

    /**
     * Initialize the map
     */
    initMap() {
        this.gmap = new google.maps.Map(this.$map, {
            center: this.defaultCenter,
            zoom: this.defaultZoom,
            streetViewControl: false,
        });
    }

    /**
     * Initialize geolocation
     */
    initGeolocation() {
        const that = this;
        
        // Try HTML5 geolocation
        if (navigator.geolocation) {
            this.$geolocation.addEventListener('click', e => {
                that.getGeolocation();
            });
        } else {
            this.$geolocation.parentNode.removeChild(this.$geolocation);
        }
    }

    /**
     * Get geolocation of user
     */
    getGeolocation() {
        navigator.geolocation.getCurrentPosition(position => {
            this.currentLocation = new google.maps.LatLng(position.coords.latitude, position.coords.longitude);

            this.changeCurrentLocation();
        });
    }

    /**
     * Initialize the autocomplete field
     */
    initAutocomplete() {
        const that = this;

        this.autocomplete = new google.maps.places.Autocomplete(this.$autocompleteField);
        this.autocomplete.setComponentRestrictions({ country: ['fr'] }); // Limit to France

        this.autocomplete.addListener('place_changed', () => {
            const place = that.autocomplete.getPlace();
            that.currentLocation = place.geometry.location;

            that.changeCurrentLocation();
        });
    }

    /**
     * Change user current location
     */
    changeCurrentLocation() {
        const that = this;

        // Remove old place marker if exists
        if (this.placeMarker) {
            this.placeMarker.setMap(null);
        }

        // Create place marker with new location
        this.placeMarker = new google.maps.Marker({
            position: this.currentLocation,
            map: this.gmap,
        });

        // Add a infoWindow on place marker
        const infoWindow = new google.maps.InfoWindow({ content: 'Ma position' });
        infoWindow.open(that.gmap, that.placeMarker);
        this.placeMarker.addListener('click', () => {
            infoWindow.open(that.gmap, that.placeMarker);
        });

        that.updateDistances();
        that.updateMarkers();
    }

    /**
     * Update all distances displayed
     */
    updateDistances() {
        this.stores.forEach(store => {
            const $distanceContainer = store.$html.querySelector('.js-map-filters-store-distance-container');
            $distanceContainer.style.display = this.currentLocation ? '' : 'none';

            if (this.currentLocation) {
                this.displayDistance(store);
            }
        });
    }

    /**
     * Display distance between current place and store according to selected travelMode
     * @param {Object} store 
     */
    displayDistance(store) {
        if (this.currentLocation) {
            const travelMode = store.$html.querySelector('.js-store-travel-mode.active').dataset.travelMode;

            const distanceMatrixService = new google.maps.DistanceMatrixService();
            distanceMatrixService.getDistanceMatrix({
                origins: [this.currentLocation],
                destinations: [store.location],
                travelMode: travelMode.toUpperCase(),
            }, (response, status) => {
                if (status !== 'OK') {
                    console.error(status);
                } else {
                    let element = response.rows[0].elements[0];

                    const $distance = store.$html.querySelector('.js-map-filters-store-distance');
                    const $duration = store.$html.querySelector('.js-map-filters-store-duration');

                    if (element.status !== 'OK') {
                        $distance.innerText = '';
                        $duration.innerText = 'Pas d\'informations';
                    } else {
                        $distance.innerText = element.distance.text;
                        $duration.innerText = element.duration.text;
                    }
                }
            });
        }
    }

    /**
     * Add stores in DOM
     */
    addStoresInDOM() {
        const that = this;

        this.stores.forEach((store, i) => {
            const $storeContainer = document.createElement('div');
            $storeContainer.classList.add('tmp');
            $storeContainer.innerHTML = store.html;
            const $html = $storeContainer.firstElementChild;
            this.$storesContainer.append($html);
            $storeContainer.parentNode.removeChild($storeContainer);

            this.stores[i].$html = $html;

            const $openingHoursContainer = store.$html.querySelector('.js-map-filters-store-opening-hours');
            const $toggler = store.$html.querySelector('.js-map-filters-store-opening-hours-toggler');

            $toggler.addEventListener('click', e => {
                $openingHoursContainer.classList.toggle('open');
            });

            const $storeTitle = store.$html.querySelector('.js-map-filters-store-title');

            $storeTitle.addEventListener('click', e => {
                if (store.$html.classList.contains('active')) {
                    that.stores.forEach(s => { s.$html.classList.remove('active'); });
                    that.gmap.fitBounds(that.bounds);
                } else {
                    that.stores.forEach(s => { s.$html.classList.remove('active'); });

                    store.$html.classList.add('active');

                    that.gmap.setCenter(store.location);
                    that.gmap.setZoom(that.clickedZoom);
                }
            });

            const that = this;

            const $travelModeButtons = store.$html.querySelectorAll('.js-store-travel-mode');

            $travelModeButtons.forEach($travelModeButton => {
                this.displayDistance(store);

                $travelModeButton.addEventListener('click', e => {
                    if (!$travelModeButton.classList.contains('active')) {
                        $travelModeButtons.forEach($tmb => { $tmb.classList.remove('active'); }); // Disable active state for other travel modes
                        $travelModeButton.classList.add('active'); // Enable active state for this travel mode

                        that.displayDistance(store);
                    }
                });
            });
        });
    }

    /**
     * Create store marker
     * @param {Object} store 
     */
    createMarker(store) {
        const that = this;
        
        const marker = new google.maps.Marker({
            store,
            position: store.location,
            map: null,
            title: store.storename,
            icon: {
                url: site_url + '/wp-content/themes/limpide/assets/img/map/marker.svg',
                size: new google.maps.Size(70, 70),
                origin: new google.maps.Point(0, 0),
                anchor: new google.maps.Point(35, 70),
            }
        });

        const infoWindow = new google.maps.InfoWindow({
            content: '<div class="map-filters infowindow">' + store.html + '</div>',
            maxWidth: '255px',
        });

        google.maps.event.addListener(marker, 'click', ((marker, infoWindow) => {
            return () => {
                // Set active state to corresponding store in DOM
                // Return to default state if already active
                let alreadyActive = false;

                that.stores.forEach(store => {
                    if (store.id === marker.store.id) {
                        if (store.$html.classList.contains('active')) {
                            alreadyActive = true;
                            store.$html.classList.remove('active');
                        } else {
                            store.$html.classList.add('active');
                        }
                    } else {
                        store.$html.classList.remove('active');
                    }
                });

                if (alreadyActive) {
                    this.gmap.fitBounds(this.bounds);
                } else {
                    that.gmap.setCenter(store.location);
                    that.gmap.setZoom(that.clickedZoom);
                }

                if (!DeviceManager.isGreaterThan(DeviceManager.getCurrentDevice(), 'lg')) {
                    if (alreadyActive) {
                        infoWindow.close(that.gmap, marker);
                    } else {
                        infoWindow.open(that.gmap, marker);
                    }
                }
            }
        })(marker, infoWindow));

        return marker;
    }

    /**
     * Update markers displayed on map
     */
    updateMarkers() {
        this.bounds = new google.maps.LatLngBounds();

        const storesToAdd = this.getStoresClosest();
        let remainingStoresIDToAdd = storesToAdd.map(store => store.id);
        
        this.stores.forEach(store => {
            if (store.marker.map !== null) {
                if (!remainingStoresIDToAdd.includes(store.id)) {
                    store.marker.setMap(null);
                    store.$html.classList.add('hidden');
                } else {
                    const index = remainingStoresIDToAdd.indexOf(store.id);
                    remainingStoresIDToAdd.splice(index, 1);

                    this.bounds.extend(store.marker.position);
                }
            }
        });

        if (this.currentLocation) {
            this.bounds.extend(this.currentLocation);
        }

        storesToAdd.forEach((store, index) => {
            if (remainingStoresIDToAdd.includes(store.id)) {
                store.marker.setMap(this.gmap);
                store.$html.classList.remove('hidden');

                this.bounds.extend(store.marker.position);
            }

            store.$html.style.order = index;
        });

        this.gmap.fitBounds(this.bounds);
    }
}

export default DynamicMap;